import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Users, 
  Search, 
  Filter, 
 
  Plus,
  Eye,
  Mail,
  Settings,
  Star,
  Crown,
  Shield,
  UserPlus,
  Calendar,
  BarChart3,
  Loader,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import CreateTeamModal from '@/components/teams/CreateTeamModal'
import TeamInviteModal from '@/components/teams/TeamInviteModal'
import TeamDetailsModal from '@/components/teams/TeamDetailsModal'
import PendingInvitations from '@/components/teams/PendingInvitations'
import SortableTable, { Column } from '@/components/common/SortableTable'
interface TeamMember {
  id: number
  name: string
  email: string
}

interface Team {
  uuid: string
  name: string
  description: string
  type: string
  created_at: string
  is_active: boolean
  member_count: number
  max_members: number
  team_lead: TeamMember | null
}

interface Team {
  id: number
  uuid: string
  name: string
  description: string
  team_type: string
  max_members: number
  member_count: number
  is_active: boolean
  created_at: string
  team_lead: {
    id: number
    name: string
    email: string
  } | null
  members: Array<{
    name: string
    role: string
  }>
}

export default function TeamsPage() {
  const navigate = useNavigate()
  const teamsEnabled = useFeatureFlag('recruiterTeams')
  const [teams, setTeams] = useState<Team[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)

  useEffect(() => {
    if (!teamsEnabled) {
      return
    }
    loadTeams()
  }, [teamsEnabled])

  const loadTeams = async () => {
    if (!teamsEnabled) return

    try {
      setLoading(true)
      const response = await apiClient.getTeams()
      if (response.success && response.data) {
        // Handle the correct response format: response.data.teams
        const teamsData = response.data.teams || []
        setTeams(Array.isArray(teamsData) ? teamsData : [])
      } else {
        setTeams([])
      }
    } catch (err) {
      console.error('Failed to load teams:', err)
      setTeams([])
    } finally {
      setLoading(false)
    }
  }

  const getTeamTypeColor = (type: string) => {
    switch (type) {
      case 'recruiting':
        return 'bg-blue-100 text-blue-800'
      case 'hiring':
        return 'bg-green-100 text-green-800'
      case 'hr':
        return 'bg-purple-100 text-purple-800'
      case 'operations':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusColor = (isActive: boolean) => {
    return isActive 
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-800'
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'lead': return <Crown className="w-4 h-4 text-yellow-600" />
      case 'admin': return <Shield className="w-4 h-4 text-blue-600" />
      default: return <Users className="w-4 h-4 text-gray-500" />
    }
  }

  const handleViewTeam = (team: Team) => {
    // Navigate to team detail page with sub-tabs
    navigate(`/recruiter/teams/${team.id}`)
  }

  const handleInviteToTeam = (team: Team) => {
    setSelectedTeam(team)
    setShowInviteModal(true)
  }

  const handleManageTeam = (teamUuid: string) => {
    navigate(`/recruiter/teams/${teamUuid}/manage`)
  }


  const handleCreateTeam = () => {
    setShowCreateModal(true)
  }

  const handleTeamCreated = () => {
    setShowCreateModal(false)
    loadTeams()
  }

  const handleInviteSent = () => {
    setShowInviteModal(false)
    // Refresh the pending invitations list to show the new invitation
    setRefreshTrigger(prev => prev + 1)
  }

  const handleInvitationResponse = () => {
    // Refresh both teams and invitations when someone accepts/declines
    loadTeams()
    setRefreshTrigger(prev => prev + 1)
  }

  if (!teamsEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] text-center space-y-4">
        <AlertCircle className="w-10 h-10 text-gray-400" />
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Teams coming soon</h1>
          <p className="text-gray-600 mt-2 max-w-lg">
            The teams workspace is disabled in this environment while we finish preparing it for production.
          </p>
        </div>
      </div>
    )
  }

  const teamColumns: Column<Team>[] = [
    {
      key: 'name',
      label: 'Team Name',
      sortable: true,
      filterable: true,
      render: (_, team) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="font-medium text-gray-900">{team.name}</div>
            <div className="text-sm text-gray-500">{team.description || 'No description'}</div>
          </div>
        </div>
      )
    },
    {
      key: 'team_type',
      label: 'Type',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'recruiting', label: 'Recruiting' },
        { value: 'hiring', label: 'Hiring' },
        { value: 'hr', label: 'HR' },
        { value: 'operations', label: 'Operations' },
        { value: 'other', label: 'Other' }
      ],
      render: (type) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTeamTypeColor(type)}`}>
          {type}
        </span>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' }
      ],
      render: (isActive) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(isActive)}`}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'team_lead',
      label: 'Team Lead',
      sortable: true,
      filterable: true,
      render: (teamLead) => teamLead ? teamLead.name : 'No lead assigned'
    },
    {
      key: 'member_count',
      label: 'Members',
      sortable: true,
      filterable: true,
      filterType: 'number',
      render: (_, team) => `${team.member_count} / ${team.max_members}`
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (date) => new Date(date).toLocaleDateString()
    },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading teams...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Teams</h1>
          <p className="text-gray-600 mt-1">Manage your recruiting teams and collaborate with colleagues</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button onClick={handleCreateTeam} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Create Team
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{Array.isArray(teams) ? teams.length : 0}</div>
              <div className="text-sm text-gray-600">Total Teams</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {Array.isArray(teams) ? teams.filter(t => t.is_active).length : 0}
              </div>
              <div className="text-sm text-gray-600">Active Teams</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <UserPlus className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {Array.isArray(teams) ? teams.reduce((acc, team) => acc + team.member_count, 0) : 0}
              </div>
              <div className="text-sm text-gray-600">Total Members</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Crown className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {Array.isArray(teams) ? teams.filter(t => t.team_lead).length : 0}
              </div>
              <div className="text-sm text-gray-600">Led Teams</div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Invitations */}
      <PendingInvitations 
        refreshTrigger={refreshTrigger}
        onInvitationResponse={handleInvitationResponse}
      />

      {/* Teams Table */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {Array.isArray(teams) ? teams.length : 0} Teams
          </h2>
        </div>
        
        {!Array.isArray(teams) || teams.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No teams</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first recruiting team.
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreateTeam}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Team
              </button>
            </div>
          </div>
        ) : (
          <SortableTable
            data={teams}
            columns={teamColumns}
            onRowClick={(team) => handleViewTeam(team)}
          />
        )}
      </div>

      {/* Modals */}
      <CreateTeamModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onTeamCreated={handleTeamCreated}
      />

      <TeamInviteModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        team={selectedTeam}
        onInviteSent={handleInviteSent}
      />

      <TeamDetailsModal
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        team={selectedTeam}
        onTeamUpdated={loadTeams}
      />
    </div>
  )
}
